import {
  CreateButton,
  Button,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  NumberInput,
  ReferenceInput,
  SelectField,
  SimpleList,
  TextField,
  TopToolbar,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import { checkPermission } from "server/security/utils";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import SecuredComponent from "server/security/SecuredComponent";
import { DormitoryRoomStatuses, MOBILE_BREAKPOINT } from "utils/constants";
import { DormitoryRoomExpositionChoices, DormitoryRoomStatusChoices } from "utils/choices";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { dormitoryRequests } from "api/dormitoryApi";
import StatusToggleDialog from "../Partials/StatusToggleDialog";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import DatagridActions from "../Partials/DatagridActions";

const listFilters = [
  <ReferenceInput
    reference={dormitoryRequests.getBuildingsSelect()}
    label="Блок"
    source="buildingId"
    alwaysOn
  >
    <DormitoryAutocompleteInput
      label="Блок"
      optionText="name"
    />
  </ReferenceInput>,
  <NumberInput
    source="floor"
    label="Етаж"
    alwaysOn
    onWheel={e => e.target.blur()}
  />,
  <NumberInput
    source="number"
    label="Номер"
    alwaysOn
    onWheel={e => e.target.blur()}
  />,
  <ReferenceInput
    reference={dormitoryRequests.getRoomTypesSelect()}
    label="Тип стая"
    source="typeId"
    alwaysOn
  >
    <DormitoryAutocompleteInput
      label="Тип стая"
      optionText="name"
    />
  </ReferenceInput>,
  <ReferenceInput
    reference={dormitoryRequests.getRoomConditionsSelect()}
    label="Състояние"
    source="conditionId"
    alwaysOn
  >
    <DormitoryAutocompleteInput
      label="Състояние"
      optionText="name"
    />
  </ReferenceInput>,
  <DormitoryAutocompleteInput
    source="exposition"
    label="Изложение"
    choices={DormitoryRoomExpositionChoices}
    alwaysOn
    emptyText="Всички"
  />,
  <DormitoryAutocompleteInput
    source="status"
    label="Статус"
    choices={DormitoryRoomStatusChoices}
    alwaysOn
    emptyText="Всички"
  />,
];

const roomStatusToggle = record => (
  <SecuredComponent permission={permissions.canManageDormitoryRooms}>
    <StatusToggleDialog
      isActive={record?.status === DormitoryRoomStatuses.Active}
      activateUrl={dormitoryRequests.activateRoom(record?.id)}
      deactivateUrl={dormitoryRequests.deactivateRoom(record?.id)}
      confirmMessage="Сигурни ли сте, че искате да смените статуса на стаята?"
      successMessage="Успешно сменихте статуса на стаята"
    />
  </SecuredComponent>
);

const roomActions = record => (
  <>
    <SecuredComponent permission={permissions.canManageDormitoryRooms}>
      <EditButton label="Редактирай" />
    </SecuredComponent>
    {roomStatusToggle(record)}
  </>
);

const RoomsTable = ({ onShowMap }) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const canManage = checkPermission(getPermissions(), permissions.canManageDormitoryRooms);

  const ListActions = () => (
    <TopToolbar>
      <Button
        label="Покажи карта"
        onClick={onShowMap}
      />
      <FilterButton />
      {canManage && <CreateButton />}
    </TopToolbar>
  );

  return (
    <List
      title="Стаи"
      exporter={false}
      filters={listFilters}
      actions={<ListActions />}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => <MobileListRow fields={[{ label: "Номер", value: record?.roomNumber }]} />}
          secondaryText={() => (
            <div>
              <MobileListRow fields={[{ label: "Етаж", value: <TextField source="floor" /> }]} />
              <MobileListRow fields={[{ label: "Блок", value: <TextField source="buildingNumber" /> }]} />
              <MobileListRow fields={[{ label: "Тип", value: <TextField source="typeDescription" /> }]} />
              <MobileListRow fields={[{ label: "Състояние", value: <TextField source="conditionDescription" /> }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Изложение",
                    value: (
                      <SelectField
                        source="exposition"
                        choices={DormitoryRoomExpositionChoices}
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: (
                      <SelectField
                        source="status"
                        choices={DormitoryRoomStatusChoices}
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={record => roomActions(record)}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="roomNumber"
            label="Номер"
            sortable={false}
          />
          <TextField
            source="floor"
            label="Етаж"
            sortable={false}
          />
          <TextField
            source="buildingNumber"
            label="Блок"
            sortable={false}
          />
          <TextField
            source="typeDescription"
            label="Тип стая"
            sortable={false}
          />
          <TextField
            source="conditionDescription"
            label="Състояние"
            sortable={false}
          />
          <SelectField
            source="exposition"
            label="Изложение"
            choices={DormitoryRoomExpositionChoices}
            sortable={false}
          />
          <SelectField
            source="status"
            label="Статус"
            choices={DormitoryRoomStatusChoices}
            sortable={false}
          />
          <FunctionField
            source="actions"
            label={false}
            sortable={false}
            render={record => <DatagridActions>{roomActions(record)}</DatagridActions>}
          />
        </Datagrid>
      )}
    </List>
  );
};

export default RoomsTable;
